/* eslint-disable prettier/prettier */

import React, { Suspense } from 'react';

import { useTranslation } from '@crehana/i18n';
import { Col, Container, Row } from '@crehana/ui';
import { useMe } from '@crehana/web/dist/ReactContexts/AuthContext';

import StructuredDataCourseList from 'Shared/SEO/StructuredData/CourseList';

import { createUrlWithSourceQueryParams } from 'Jsx/Analytics/Amplitude';
import {
  CATALOG_AMPLITUDE_PAGE_NAME,
  ENROLLMENT_URL_SOURCES,
} from 'Jsx/Catalog/constants';
import type { CourseRecommendedFragment } from 'Jsx/Shared/CourseCardV3/graphql/types/CourseRecommendedFragment';
import type { CoursesFragment } from 'Jsx/Shared/CourseCardV3/graphql/types/CoursesFragment';
import useFeatureFlagQuery from 'Jsx/Shared/Hooks/useFeatureFlagQuery';
import { formatProduct } from 'Jsx/Utils/buyProduct';

import type { CourseTag } from '../CourseCardV3/types/Course';
import { CourseCardSkeleton } from '../CourseCardV3/skeletons';
import { CourseCardV3 } from '../CourseCardV3';

import { COMMON_COLS } from './constants';

const FreeVisualRest = React.lazy(() => import('../VisualRest/FreeVisualRest'));

interface CourseListProps {
  loading?: boolean;
  skeletonItems?: number;
  isMobile?: boolean;
  courses: (CoursesFragment | CourseRecommendedFragment)[];
  withPriority?: boolean;
  onClickCourse?: (course: CoursesFragment) => void;
  showVisualRest?: boolean;
}

const CourseList: React.FC<CourseListProps> = ({
  loading,
  courses,
  skeletonItems = 0,
  isMobile = false,
  withPriority,
  showVisualRest,
  onClickCourse,
}) => {
  const { me: user } = useMe();
  const {
    isActive: isPreviewTrialFeatureEnabled,
    loading: loadingFeatureFlag,
  } = useFeatureFlagQuery({
    flagKey: 'course_preview_trial',
  });
  const { t } = useTranslation();
  const isOrganization = !!user?.hasOrganization;

  const clickTitleHandler = (
    course: CoursesFragment | CourseRecommendedFragment,
  ) => {
    const courseUrl = createUrlWithSourceQueryParams({
      url: course?.url || '',
      source: CATALOG_AMPLITUDE_PAGE_NAME,
    });

    if (onClickCourse) {
      // @ts-expect-error
      onClickCourse(course);
    } else {
      window.location.href = courseUrl;
    }
  };

  const getCourseTag = (courseTag: CourseTag | null): CourseTag | null => {
    if (
      courseTag &&
      courseTag.backgroundColor &&
      courseTag.textColor &&
      courseTag.textLabel
    ) {
      const formattedTag = {
        icon: null,
        name: courseTag?.name || '',
        textLabel: t('CATALOG_COURSE_CARD_TAG_LABEL', {
          defaultValue: courseTag.textLabel,
        }),
        backgroundColor: t('CATALOG_COURSE_CARD_TAG_BG_COLOR', {
          defaultValue: courseTag.backgroundColor || '',
        }),
        textColor: t('CATALOG_COURSE_CARD_TAG_TEXT_COLOR', {
          defaultValue: courseTag.textColor || '',
        }),
      };

      return formattedTag;
    }
    return null;
  };

  return (
    <>
      {/* @ts-expect-error */}
      <StructuredDataCourseList courses={courses} />

      <div className="flex flex-col lg:mb-32" id="catalog-course-list">
        <Container className="w-full lg:mb-32" full>
          {loading || loadingFeatureFlag ? (
            <Row>
              {Array.from(Array(skeletonItems)).map((_, i) => (
                <Col
                  key={i.toString()}
                  xs={COMMON_COLS.xs}
                  sm={COMMON_COLS.sm}
                  md={COMMON_COLS.md}
                  lg={COMMON_COLS.lg}
                >
                  <CourseCardSkeleton />
                </Col>
              ))}
            </Row>
          ) : (
            <Row>
              {/* TODO: olivera. This next lines contains importa ts exceptions.
              Search or recommended courses query fragments needs
            to support isPreviewTrial and canEnrollCoursePreviewTrial before the
            final release.
             */}
              {courses.map((c, index) => {
                const getIsAvailableForPreviewTrial = () => {
                  if (
                    isPreviewTrialFeatureEnabled &&
                    !c.isEnrollment &&
                    // @ts-expect-error
                    c.isPreviewTrial
                  ) {
                    if (user) {
                      // @ts-expect-error
                      return c.canEnrollCoursePreviewTrial;
                    }
                    // @ts-expect-error
                    return c.isPreviewTrial;
                  }

                  return false;
                };

                const showHereVisualRest =
                  showVisualRest && index !== 0 && index % 8 === 0;

                return [
                  <Col
                    key={c.originalId}
                    xs={COMMON_COLS.xs}
                    sm={COMMON_COLS.sm}
                    md={COMMON_COLS.md}
                    lg={COMMON_COLS.lg}
                  >
                    {/* @ts-expect-error */}
                    <CourseCardV3
                      {...c}
                      courseTag={getCourseTag(c.courseTag)}
                      // @ts-expect-error
                      product={formatProduct(c, 'course')}
                      promoImage={c.promoImage!}
                      promoImageAnimated={c.promoImageAnimated!}
                      isOrganization={isOrganization}
                      courseCardPage="CATALOG"
                      priority={withPriority && index < 3}
                      clickTitleHandler={() => clickTitleHandler(c)}
                      customOnClickImage={
                        isMobile && onClickCourse
                          ? // @ts-expect-error
                            () => onClickCourse(c)
                          : undefined
                      }
                      source={CATALOG_AMPLITUDE_PAGE_NAME}
                      sourceDetail={ENROLLMENT_URL_SOURCES.COURSE_LIST}
                      isAvailableForPreviewTrial={getIsAvailableForPreviewTrial()}
                    />
                  </Col>,
                  showHereVisualRest && (
                    <Col
                      key={'visual_rest_'.concat(index.toString())}
                      totalCols={12}
                    >
                      <Suspense fallback={<div style={{ height: 280 }} />}>
                        <FreeVisualRest />
                      </Suspense>
                    </Col>
                  ),
                ];
              })}
            </Row>
          )}
        </Container>
      </div>
    </>
  );
};

export default CourseList;
